import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaHandHoldingUsd } from 'react-icons/fa';

import { moisCourant } from '../../components/Utils/mois';
import SelecteurMois from '../../components/Utils/SelecteurMois';
import { apiService } from '../../components/Config/api.service';
import SelecteurPieces, { verifierPieces } from './SelecteurPieces';

/**
 * Dépôt d'un prêt au salarié par son titulaire (Yros/Kavero#410).
 *
 * Un capital, une période de remboursement et un motif. Le motif est pris dans
 * une **liste fermée** et jamais saisi librement : un motif libre peut révéler
 * la religion, la santé ou la situation familiale, que la loi 2008-12 protège
 * en son article 40. Les cinq entrées disent assez pour instruire la demande
 * sans rien collecter de plus.
 *
 * L'écran ne vérifie que ce qu'il peut voir : un montant qui n'est pas un
 * nombre strictement positif, une période absente ou renversée, un motif hors
 * liste. Le plafond légal, la quotité disponible et l'encours vivent dans
 * Odoo, et leurs refus s'affichent mot pour mot.
 *
 * Le prêt reste « en attente » après le dépôt, tant que sa reconnaissance de
 * dette n'est pas jointe : une retenue sans autorisation écrite est nulle et
 * porte intérêt au profit du salarié (L.132). L'écran le dit plutôt que de
 * laisser croire à un oubli.
 */

const CLASSE_CHAMP =
  'mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 font-medium text-sm';

// Même liste, et dans le même ordre, que celle qu'Odoo fait respecter. Deux
// listes qui divergent feraient découvrir la bonne par un refus.
const MOTIFS = ['famille', 'logement', 'scolarite', 'equipement', 'autre'];

function DemandePret({ onDepot }) {
  const { t } = useTranslation();
  const [montant, setMontant] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [pieces, setPieces] = useState([]);

  const soumettre = async (event) => {
    event.preventDefault();
    setErreur(null);
    setSucces(null);

    const montantNombre = Number(montant);
    if (!montant || !Number.isFinite(montantNombre) || montantNombre <= 0) {
      setErreur(t('demandePret.montantInvalide'));
      return;
    }
    if (!debut || !fin) {
      setErreur(t('demandePret.champsManquants'));
      return;
    }
    // Comparaison de chaînes « AAAA-MM » : elles s'ordonnent comme les mois
    // qu'elles désignent, tant que le format est celui du contrat.
    if (debut < moisCourant()) {
      setErreur(t('demandePret.moisPasse'));
      return;
    }
    if (fin < debut) {
      setErreur(t('demandePret.periodeInvalide'));
      return;
    }
    if (!MOTIFS.includes(motif)) {
      setErreur(t('demandePret.motifManquant'));
      return;
    }

    const refusPieces = verifierPieces(pieces, t);
    if (refusPieces) {
      setErreur(refusPieces);
      return;
    }

    setEnvoi(true);
    try {
      const demande = {
        type: 'pret',
        contenu: { montant: montantNombre, debut, fin, motif },
      };
      // Sans pièce, l'appel est celui d'avant : un seul argument.
      const reponse = pieces.length
        ? await apiService.creerDemandeRh(demande, pieces)
        : await apiService.creerDemandeRh(demande);
      if (reponse && reponse.ok) {
        setSucces(t('demandePret.succes'));
        setMontant('');
        setDebut('');
        setFin('');
        setMotif('');
        setPieces([]);
        if (onDepot) onDepot();
      } else {
        setErreur(reponse?.message || t('demandePret.erreurGenerique'));
      }
    } catch (e) {
      setErreur(e?.response?.data?.message || t('demandePret.erreurGenerique'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md border border-gray-100 space-y-4">
      <div className="flex items-center gap-2">
        <FaHandHoldingUsd className="text-[--color-primary]" />
        <h2 className="text-base sm:text-lg font-semibold">{t('demandePret.titre')}</h2>
      </div>
      <p className="text-sm text-gray-600">{t('demandePret.explication')}</p>

      <form data-testid="pret-formulaire" onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm font-medium">
            {t('demandePret.montant')}
            <input
              type="number"
              inputMode="numeric"
              min="1"
              step="any"
              data-testid="pret-montant"
              value={montant}
              onChange={(e) => setMontant(e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block text-sm font-medium">
            {t('demandePret.motif')}
            <select
              data-testid="pret-motif"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">{t('demandePret.motifChoisir')}</option>
              {MOTIFS.map((code) => (
                <option key={code} value={code}>
                  {t(`demandePret.motifs.${code}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium">
            {t('demandePret.debut')}
            {/* « AAAA-MM » est la forme que le contrat attend. Safari et Firefox
                de bureau n'ont pas `type="month"` : le sélecteur y bascule sur
                deux listes qui composent la même valeur (Yros/Kavero#426). */}
            <SelecteurMois
              testId="pret-debut"
              min={moisCourant()}
              value={debut}
              onChange={setDebut}
              className={CLASSE_CHAMP}
            />
          </label>
          <label className="block text-sm font-medium">
            {t('demandePret.fin')}
            <SelecteurMois
              testId="pret-fin"
              min={debut || moisCourant()}
              value={fin}
              onChange={setFin}
              className={CLASSE_CHAMP}
            />
          </label>
        </div>

        <SelecteurPieces idPrefix="pret" fichiers={pieces} onChange={setPieces} />

        {erreur && (
          <p
            data-testid="pret-erreur"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {erreur}
          </p>
        )}
        {succes && (
          <p
            data-testid="pret-succes"
            className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
          >
            {succes}
          </p>
        )}

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            data-testid="pret-soumettre"
            disabled={envoi}
            className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FaPaperPlane size={14} />
            {envoi ? t('demandePret.envoi') : t('demandePret.envoyer')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DemandePret;
